import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { openSecureMedia } from '@/lib/secureMedia';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { FileCheck, AlertTriangle, Clock, CheckCircle2, XCircle, Send, ChevronLeft, ChevronRight, Bell, Shield } from 'lucide-react';

const ALERT_COLORS = { EXPIRED: 'bg-red-600 text-white', CRITICAL: 'bg-red-500 text-white', URGENT: 'bg-amber-500 text-white', WARNING: 'bg-yellow-400 text-black', OK: 'bg-green-500 text-white' };
const STATUS_COLORS = { PENDING: 'bg-slate-400 text-white', APPROVED: 'bg-green-500 text-white', REJECTED: 'bg-red-500 text-white', EXPIRED: 'bg-red-600 text-white' };

export default function DocumentReviewDashboardPage() {
  return (
    <div className="space-y-6" data-testid="document-review-dashboard">
      <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><FileCheck className="w-6 h-6" />Document Review & Expiry Tracking</h1>
      <Tabs defaultValue="review">
        <TabsList>
          <TabsTrigger value="review" data-testid="tab-review"><FileCheck className="w-4 h-4 mr-1" />Document Review</TabsTrigger>
          <TabsTrigger value="expiry" data-testid="tab-expiry"><AlertTriangle className="w-4 h-4 mr-1" />Expiry Tracker</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts"><Bell className="w-4 h-4 mr-1" />Alert History</TabsTrigger>
        </TabsList>
        <TabsContent value="review" className="mt-4"><DocumentReviewTab /></TabsContent>
        <TabsContent value="expiry" className="mt-4"><ExpiryTrackerTab /></TabsContent>
        <TabsContent value="alerts" className="mt-4"><AlertHistoryTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function DocumentReviewTab() {
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [reviewDoc, setReviewDoc] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const toggleSelect = (id) => setSelectedDocs(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAllPending = () => setSelectedDocs(new Set(docs.filter(d => d.status === 'PENDING').map(d => d.id)));

  const bulkApprove = async () => {
    setBulkLoading(true);
    let success = 0;
    for (const id of selectedDocs) {
      try { await api.post(`/admin/document/review/${id}`, { status: 'APPROVED', notes: 'Bulk approved' }); success++; } catch {}
    }
    toast.success(`${success} documents approved`);
    setSelectedDocs(new Set()); setBulkLoading(false); load();
  };

  const bulkReject = async () => {
    setBulkLoading(true);
    let success = 0;
    for (const id of selectedDocs) {
      try { await api.post(`/admin/document/review/${id}`, { status: 'REJECTED', notes: 'Bulk rejected — requires resubmission' }); success++; } catch {}
    }
    toast.success(`${success} documents rejected`);
    setSelectedDocs(new Set()); setBulkLoading(false); load();
  };

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '20' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const r = await api.get(`/admin/onboarding/documents?${params}`);
      setDocs(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Failed to load documents'); }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleReview = async () => {
    if (!reviewDoc || !reviewStatus) return;
    try {
      await api.post(`/admin/document/review/${reviewDoc.id}`, { status: reviewStatus, notes: reviewNotes });
      toast.success(`Document ${reviewStatus.toLowerCase()}`);
      setReviewDoc(null); setReviewStatus(''); setReviewNotes(''); load();
    } catch (e) { toast.error(e.response?.data?.error?.message || 'Review failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center flex-wrap">
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="PENDING">Pending</SelectItem><SelectItem value="APPROVED">Approved</SelectItem><SelectItem value="REJECTED">Rejected</SelectItem></SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{total} documents</span>
        {docs.some(d => d.status === 'PENDING') && (
          <div className="flex gap-2 ml-auto">
            <Button size="sm" variant="outline" onClick={selectAllPending} data-testid="select-all-pending">Select All Pending ({docs.filter(d => d.status === 'PENDING').length})</Button>
            {selectedDocs.size > 0 && (
              <>
                <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={bulkApprove} disabled={bulkLoading} data-testid="bulk-approve-btn">
                  {bulkLoading ? 'Processing...' : `Approve ${selectedDocs.size}`}
                </Button>
                <Button size="sm" variant="destructive" onClick={bulkReject} disabled={bulkLoading} data-testid="bulk-reject-btn">
                  Reject {selectedDocs.size}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2">
        {docs.map(d => (
          <Card key={d.id} className={selectedDocs.has(d.id) ? 'border-primary' : ''}>
            <CardContent className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3 min-w-0">
                {d.status === 'PENDING' && <input type="checkbox" checked={selectedDocs.has(d.id)} onChange={() => toggleSelect(d.id)} className="rounded h-4 w-4 shrink-0" />}
                <Badge className={STATUS_COLORS[d.status]}>{d.status}</Badge>
                <div className="min-w-0">
                  <p className="font-medium text-sm">{d.document_type?.replace(/_/g, ' ')}</p>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {d.candidate_name && <span className="mr-3">{d.candidate_name}</span>}
                    {d.document_number && <span className="mr-3">#{d.document_number}</span>}
                    {d.expiry_date && <span>Expires: {d.expiry_date?.slice(0, 10)}</span>}
                  </div>
                  {d.file_name && <p className="text-xs text-muted-foreground">{d.file_name} ({((d.file_size || 0) / 1024).toFixed(0)}KB)</p>}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                {d.file_url && <Button size="sm" variant="outline" onClick={async () => {
                  try {
                    await openSecureMedia(d.file_url);
                  } catch { toast.error('Unable to open document'); }
                }}>View</Button>}
                {d.status === 'PENDING' && (
                  <>
                    <Button size="sm" variant="default" className="bg-green-600 hover:bg-green-700" onClick={() => { setReviewDoc(d); setReviewStatus('APPROVED'); setReviewNotes(''); }}>Approve</Button>
                    <Button size="sm" variant="destructive" onClick={() => { setReviewDoc(d); setReviewStatus('REJECTED'); setReviewNotes(''); }}>Reject</Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {docs.length === 0 && <p className="text-center text-muted-foreground py-8">No documents to review</p>}
      </div>

      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">Page {page}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" disabled={docs.length < 20} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <Dialog open={!!reviewDoc} onOpenChange={() => setReviewDoc(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{reviewStatus === 'APPROVED' ? 'Approve' : 'Reject'} Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm"><strong>{reviewDoc?.document_type?.replace(/_/g, ' ')}</strong> for {reviewDoc?.candidate_name}</p>
            <div><Textarea placeholder="Review notes (optional)..." value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDoc(null)}>Cancel</Button>
            <Button variant={reviewStatus === 'APPROVED' ? 'default' : 'destructive'} onClick={handleReview}>{reviewStatus === 'APPROVED' ? 'Approve' : 'Reject'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExpiryTrackerTab() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [alertFilter, setAlertFilter] = useState('all');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ days_ahead: '90' });
      if (alertFilter !== 'all') params.set('alert_type', alertFilter);
      const r = await api.get(`/admin/documents/expiry-tracker?${params}`);
      setItems(r.data.data?.items || []);
      setSummary(r.data.data?.summary || null);
    } catch { toast.error('Failed to load expiry data'); }
  }, [alertFilter]);

  useEffect(() => { load(); }, [load]);

  const sendReminders = async () => {
    setSending(true);
    try {
      const r = await api.post('/admin/documents/send-expiry-reminders');
      toast.success(`${r.data.data.reminders_sent} reminders sent`);
    } catch { toast.error('Failed to send reminders'); }
    setSending(false);
  };

  return (
    <div className="space-y-4">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold">{summary.total}</p><p className="text-xs text-muted-foreground">Total Tracked</p></CardContent></Card>
          <Card className="border-red-200"><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-red-600">{summary.expired}</p><p className="text-xs text-muted-foreground">Expired</p></CardContent></Card>
          <Card className="border-red-100"><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-red-500">{summary.critical}</p><p className="text-xs text-muted-foreground">Critical (7d)</p></CardContent></Card>
          <Card className="border-amber-100"><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-amber-500">{summary.urgent}</p><p className="text-xs text-muted-foreground">Urgent (30d)</p></CardContent></Card>
          <Card className="border-yellow-100"><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-yellow-600">{summary.warning}</p><p className="text-xs text-muted-foreground">Warning (60d)</p></CardContent></Card>
        </div>
      )}

      <div className="flex gap-3 items-center">
        <Select value={alertFilter} onValueChange={setAlertFilter}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Alert Level" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Levels</SelectItem><SelectItem value="EXPIRED">Expired</SelectItem><SelectItem value="CRITICAL">Critical</SelectItem><SelectItem value="URGENT">Urgent</SelectItem><SelectItem value="WARNING">Warning</SelectItem></SelectContent>
        </Select>
        <Button variant="outline" onClick={sendReminders} disabled={sending} data-testid="send-reminders-btn">
          <Send className="w-4 h-4 mr-2" />{sending ? 'Sending...' : 'Send Reminders'}
        </Button>
      </div>

      <div className="space-y-2">
        {items.map((item, i) => (
          <Card key={item.id || i} className={item.alert_type === 'EXPIRED' ? 'border-red-300' : item.alert_type === 'CRITICAL' ? 'border-red-200' : ''}>
            <CardContent className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3 min-w-0">
                <Badge className={ALERT_COLORS[item.alert_type]}>{item.alert_type}</Badge>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{item.document_type?.replace(/_/g, ' ')}</p>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    <span className="mr-3">{item.person_name || 'Unknown'}</span>
                    <span className="mr-3">Source: {item.source}</span>
                    {item.document_number && <span>#{item.document_number}</span>}
                  </div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-mono">{item.expiry_date?.slice(0, 10)}</p>
                <p className={`text-xs font-medium ${item.days_until_expiry < 0 ? 'text-red-600' : item.days_until_expiry <= 7 ? 'text-red-500' : item.days_until_expiry <= 30 ? 'text-amber-500' : 'text-muted-foreground'}`}>
                  {item.days_until_expiry < 0 ? `${Math.abs(item.days_until_expiry)}d overdue` : `${item.days_until_expiry}d remaining`}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-center text-muted-foreground py-8">No documents expiring in the next 90 days</p>}
      </div>
    </div>
  );
}

function AlertHistoryTab() {
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get(`/admin/documents/expiry-alerts?page=${page}&pageSize=20`)
      .then(r => { setAlerts(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => {});
  }, [page]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{total} alerts sent</p>
      <div className="space-y-2">
        {alerts.map(a => (
          <Card key={a.id}>
            <CardContent className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <Badge className={ALERT_COLORS[a.alert_type]}>{a.alert_type}</Badge>
                <div>
                  <p className="text-sm font-medium">{a.document_type?.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-muted-foreground">Expiry: {a.expiry_date} · {a.days_remaining}d remaining</p>
                </div>
              </div>
              <div className="text-right">
                <Badge variant="outline">{a.status}</Badge>
                <p className="text-xs text-muted-foreground mt-0.5">{a.sent_date}</p>
              </div>
            </CardContent>
          </Card>
        ))}
        {alerts.length === 0 && <p className="text-center text-muted-foreground py-8">No alerts sent yet</p>}
      </div>
      <div className="flex justify-between">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
        <Button size="sm" variant="outline" disabled={alerts.length < 20} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
